import { useStaticQuery, graphql } from "gatsby"
import { GatsbyImage, getImage } from "gatsby-plugin-image"

/**
 * One static query for every image the UK schools page needs, so each section
 * doesn't carry its own. Gatsby hoists static queries at build time, so this
 * has to stay a literal — don't parameterise it.
 */
const useUkImages = () =>
  useStaticQuery(graphql`
    {
      journey: file(relativePath: { eq: "schools/AdobeStock_183480624.jpeg" }) {
        childImageSharp {
          gatsbyImageData(quality: 90, width: 1000)
        }
      }
      wall: file(relativePath: { eq: "schools/AdobeStock_340231009.jpeg" }) {
        childImageSharp {
          gatsbyImageData(quality: 90, width: 1000)
        }
      }
      response: file(
        relativePath: { eq: "schools/AdobeStock_195624071.jpeg" }
      ) {
        childImageSharp {
          gatsbyImageData(quality: 90, width: 1000)
        }
      }
      faq: file(relativePath: { eq: "schools/AdobeStock_329694923.jpeg" }) {
        childImageSharp {
          gatsbyImageData(quality: 90, width: 1000)
        }
      }
      videoCover: file(
        relativePath: { eq: "schools/uk-schools-video-cover.png" }
      ) {
        childImageSharp {
          gatsbyImageData(quality: 90, width: 1200)
        }
      }
      overviewVideoCover: file(
        relativePath: { eq: "schools/uk-schools-overview-video-cover.jpg" }
      ) {
        childImageSharp {
          gatsbyImageData(quality: 90, width: 1600)
        }
      }
      threats: file(relativePath: { eq: "schools/uk-threats.jpg" }) {
        childImageSharp {
          gatsbyImageData(quality: 90, width: 1000)
        }
      }
      mockLearn: file(relativePath: { eq: "schools/uk-mock-learn.png" }) {
        childImageSharp {
          gatsbyImageData(quality: 90, width: 700, placeholder: NONE)
        }
      }
      mockGet: file(relativePath: { eq: "schools/uk-mock-get.png" }) {
        childImageSharp {
          gatsbyImageData(quality: 90, width: 700, placeholder: NONE)
        }
      }
      mockGive: file(relativePath: { eq: "schools/uk-mock-give.png" }) {
        childImageSharp {
          gatsbyImageData(quality: 90, width: 700, placeholder: NONE)
        }
      }
      mockImpact: file(relativePath: { eq: "schools/uk-mock-impact.png" }) {
        childImageSharp {
          gatsbyImageData(quality: 90, width: 700, placeholder: NONE)
        }
      }
    }
  `)

type Slot =
  | "journey"
  | "wall"
  | "response"
  | "faq"
  | "videoCover"
  | "overviewVideoCover"
  | "threats"
  | "mockLearn"
  | "mockGet"
  | "mockGive"
  | "mockImpact"

const makeImage =
  (slot: Slot, objectPosition = "center") =>
  // `className` is required: Divided (and everything built on it) always
  // passes one, and making it optional trips react/require-default-props.
  ({ className }: { className: string }) => {
    const data = useUkImages()
    return (
      <GatsbyImage
        image={getImage(data[slot])}
        className={className}
        imgStyle={{ objectFit: "cover", objectPosition }}
        alt=""
      />
    )
  }

/**
 * Product mockups behave differently from photography: they are shots of a
 * device with its own edges, so cropping one to fill a box cuts the device in
 * half. These size to fit and keep their whole frame.
 *
 * They also carry meaning, so unlike the decorative photography above they take
 * a real alt text rather than an empty one.
 */
const makeMockup =
  (slot: Slot) =>
  ({ className, alt }: { className?: string; alt: string }) => {
    const data = useUkImages()
    return (
      <GatsbyImage
        image={getImage(data[slot])}
        className={className}
        imgStyle={{ objectFit: "contain" }}
        alt={alt}
      />
    )
  }

export const JourneyImage = makeImage("journey")
export const WallImage = makeImage("wall", "top")
export const ResponseImage = makeImage("response")
export const FaqImage = makeImage("faq")
export const VideoCoverImage = makeImage("videoCover")
export const OverviewVideoCoverImage = makeImage("overviewVideoCover")
export const ThreatsImage = makeImage("threats", "center 30%")

export const MockupImages = {
  learn: makeMockup("mockLearn"),
  get: makeMockup("mockGet"),
  give: makeMockup("mockGive"),
  impact: makeMockup("mockImpact"),
}
